import { useState, useEffect, useContext } from "react";
import {
  filterRecommendedArticles,
  addViewedArticle,
} from "./SwipeArticleUtils";
import { ArticleContext } from "../../Context";
import { publicationTypes } from "common/context/Publication/PublicationContext";
import { PublicationContext } from "common/context/Publication";
import SwipeOverlayHeader from "./SwipeOverlayHeader";
import ArticleImage from "../../ArticleImage";
import { useRouter } from "next/router";
import initSwipeNavigation from "./initSwipe";
import fetchArticlesIfNeeded from "./fetchArticles";
import {
  HeroWrapper,
  NextButton,
  SwipeDiv,
  Wrapper,
} from "./SwipeOverlay.styled";

export const LOADING_DIV_ID = "swipe-overlay";

const SwipeOverlay = () => {
  const router = useRouter();
  const { refactoredArticle } = useContext(ArticleContext);
  const { publication, config } =
    useContext<publicationTypes>(PublicationContext);

  const sectionRoot = Number(refactoredArticle?.hierarchy[0]?.hierarchy[0]?.id);

  const currentArticleId = Number(refactoredArticle?.id);

  const [nextArticle, setNextArticle] = useState<any>(null);

  useEffect(() => {
    if (currentArticleId) {
      addViewedArticle(currentArticleId);
      fetchArticlesIfNeeded({
        currentArticleId,
        setNextArticle,
        publication,
        sectionId: sectionRoot,
      });
    }
  }, [currentArticleId]);

  useEffect(() => {
    if (nextArticle?.path) {
      router.prefetch(nextArticle.path); // Prefetch via Next.js

      // 1. Add <link rel="prefetch"> if it doesn't already exist (Safari, Firefox, etc.)
      const existingPrefetch = document.querySelector(
        `link[rel="prefetch"][href="${nextArticle.path}"]`,
      );

      if (!existingPrefetch) {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = nextArticle.path;
        document.head.appendChild(link);
      }

      // 2. Add speculationrules script if supported (Chromium)
      const speculationScript = document.createElement("script");
      speculationScript.type = "speculationrules";
      speculationScript.textContent = JSON.stringify({
        prefetch: [
          // We do not want to use prerender as it will load next pages ads prematurely
          {
            source: "list",
            urls: [`${nextArticle.path}`],
            eagerness: "immediate",
          },
        ],
      });
      document.head.appendChild(speculationScript);
    }
  }, [nextArticle?.path]);

  useEffect(() => {
    const cleanup = initSwipeNavigation(nextArticle?.path || null);
    return cleanup;
  }, [nextArticle]);

  const handleNextArticleClick = () => {
    const loadingDiv = document.getElementById(LOADING_DIV_ID);
    if (!loadingDiv || !nextArticle?.path) return;

    loadingDiv.style.transform = "translateX(0)"; // Slide in

    // Wait for transition to finish (250ms matches CSS)
    setTimeout(() => {
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          window.location.href = nextArticle.path;
        });
      } else {
        window.location.href = nextArticle.path;
      }
    }, 250);
  };

  if (!nextArticle?.path) return null;

  return (
    <SwipeDiv id={LOADING_DIV_ID} active={true}>
      <Wrapper>
        <>
          <SwipeOverlayHeader
            title={nextArticle.title}
            lead={nextArticle.standfirst}
            path={nextArticle.path}
          />
          <HeroWrapper>
            <ArticleImage
              image={{ url: nextArticle.imageUrl }}
              isHeroOverride={true}
            />
          </HeroWrapper>
          <NextButton onClick={handleNextArticleClick} id="next-article-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              fill="white"
              viewBox="0 0 24 24"
            >
              <path d="M13.172 12l-4.95-4.95 1.414-1.414L16 12l-6.364 6.364-1.414-1.414z" />
            </svg>
          </NextButton>
        </>
      </Wrapper>
    </SwipeDiv>
  );
};

export default SwipeOverlay;
